import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Timestamp } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';

import { ModalService } from '../../../core/services/modal.service';
import { LoadService } from '../../../core/services/load.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { LoadWithView } from '../../../core/models/load.model';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { ButtonGroupComponent } from '../../../shared/ui/forms/button-group/button-group.component';
import { NumberInputComponent } from '../../../shared/ui/forms/number-input/number-input.component';
import { FormFieldComponent } from '../../../shared/ui/forms/form-field/form-field.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';

@Component({
  selector: 'app-tonu-decision-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    ButtonGroupComponent,
    NumberInputComponent,
    FormFieldComponent,
    InputComponent,
    TextComponent,
    BadgeComponent,
    LucideIconComponent,
  ],
  templateUrl: './tonu-decision-modal.component.html',
})
export class TonuDecisionModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  readonly load = (this.modalService.state() as any)?.data?.load as LoadWithView;
  readonly onSuccess = (this.modalService.state() as any)?.data?.onSuccess;

  readonly canEditBilling = this.authService.canEditBilling;
  readonly isSaving = signal(false);

  readonly form: FormGroup;

  readonly decisionOptions = [
    { value: 'eligible', label: 'Eligible for TONU ($)' },
    { value: 'not_applicable', label: 'Waived / Not Applicable' },
  ];

  constructor() {
    const existingTonu = this.load?.view?.tonuDecision;
    const initialStatus = existingTonu?.status === 'not_applicable' ? 'not_applicable' : 'eligible';
    const initialAmount = existingTonu?.amount ?? 150;
    const initialNotes = existingTonu?.decisionNotes ?? '';

    this.form = this.fb.group({
      decision: [initialStatus, [Validators.required]],
      amount: [initialAmount, [Validators.required, Validators.min(0)]],
      notes: [initialNotes],
    });

    if (!this.canEditBilling()) {
      this.form.disable();
    }
  }

  get isEligible(): boolean {
    return this.form.get('decision')?.value === 'eligible';
  }

  get originalBookedRate(): number {
    const view = this.load?.view;
    return Number(view?.pricing?.baseRate ?? (view as any)?.bookedAtRate ?? 0);
  }

  close() {
    this.modalService.close();
  }

  async save() {
    if (this.form.invalid || !this.canEditBilling()) return;

    this.isSaving.set(true);
    const { decision, amount, notes } = this.form.getRawValue();

    try {
      const companyId = this.authService.companyId();
      if (!companyId) throw new Error('No active company found');

      const existingTonu = this.load?.view?.tonuDecision;
      const user = this.authService.appUser();
      const decidedBy = user?.fullName || this.authService.user()?.displayName || user?.id || 'Accounting';
      const tonuAmount = decision === 'eligible' ? Number(amount) : 0;

      const updatedTonuDecision = {
        status: decision as 'eligible' | 'not_applicable',
        amount: tonuAmount,
        reason: existingTonu?.reason || 'Cancelled after dispatch',
        cancelledAt: existingTonu?.cancelledAt || null,
        cancelledBy: existingTonu?.cancelledBy || null,
        decidedBy,
        decidedAt: Timestamp.now(),
        decisionNotes: notes?.trim() || null,
      };

      const existingPricing = this.load?.view?.pricing || {};
      const bookedAtRate = (this.load?.view as any)?.bookedAtRate;
      const updatedPricing = {
        ...existingPricing,
        ...(existingPricing.baseRate == null && bookedAtRate != null
          ? { baseRate: Number(bookedAtRate) }
          : {}),
        tonu: tonuAmount,
      };

      const currentBilling = this.load?.view?.billingStatus;
      const isAlreadyInvoiced =
        !!this.load?.view?.invoiceId ||
        currentBilling === 'INVOICED' ||
        currentBilling === 'PAID' ||
        currentBilling === 'PARTIALLY_PAID' ||
        currentBilling === 'OVERDUE' ||
        currentBilling === 'PAYMENT_PENDING';

      const viewPatch: Record<string, any> = {
        tonuDecision: updatedTonuDecision,
        pricing: updatedPricing,
        ...(!isAlreadyInvoiced
          ? { billingStatus: decision === 'eligible' ? 'READY_TO_INVOICE' : 'NOT_APPLICABLE' }
          : {}),
      };

      await firstValueFrom(
        this.loadService.updateCompanyView(this.load.id, companyId, viewPatch)
      );

      if (this.load) {
        this.load.view = {
          ...(this.load.view || ({ loadId: this.load.id, companyId } as any)),
          ...viewPatch,
        };
      }

      this.toastService.show(
        'success',
        decision === 'eligible'
          ? `TONU fee of $${tonuAmount} recorded and ready to invoice.`
          : 'TONU marked as not applicable / waived.'
      );

      this.modalService.close();
      this.onSuccess?.(viewPatch);
    } catch (err: any) {
      console.error('Failed to record TONU decision:', err);
      this.toastService.show('error', err.message || 'Failed to record TONU decision.');
    } finally {
      this.isSaving.set(false);
    }
  }
}
